import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../API';
import { useAuth } from '../../providers/AuthContext';
import ProjectItemListWithProgress from './ProjectItemListWithProgress';
import {
    PROJECT_LIFECYCLE,
    normalizeProjectLifecycleStatus,
} from './projectLifecycle';
import RoleBadge from './roleUtils';
import ProjectsHubBackLink from './hub/ProjectsHubBackLink';
import styles from './ProjectHomepage.module.css';

function ProjectHomepage() {
    const { folderId } = useParams();
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();

    const [doc, setDoc] = useState(null);
    const [items, setItems] = useState([]);
    const [firstItemId, setFirstItemId] = useState(null);
    const [progressMap, setProgressMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        setLoading(true);
        setError(null);

        const projectPromise = api.get(`projects/${folderId}`, {});
        const itemsPromise = api.get(`projects/${folderId}/items`, {});

        Promise.all([projectPromise, itemsPromise])
            .then(([projectRes, itemsRes]) => {
                if (projectRes.status === 200) {
                    setDoc(projectRes.data);
                } else {
                    setError(projectRes.data?.message || 'Failed to load project.');
                }
                // Items may be 403 when project is draft and user is not admin
                const itemList = itemsRes.status === 200 ? (itemsRes.data?.items ?? []) : [];
                setItems(itemList);
                if (itemList.length > 0) {
                    setFirstItemId(String(itemList[0]._id));
                }
                // Fetch progress for annotators so "Your progress" can show status
                if (projectRes.status === 200 && itemList.length > 0 && currentUser) {
                    const participants = projectRes.data?.girderProjects?.participants ?? [];
                    const myEntry = participants.find((p) => String(p.userId) === String(currentUser._id));
                    const isAnnotator = myEntry?.roles?.includes('annotator');
                    if (isAnnotator) {
                        api.get(`projects/${folderId}/progress`, {})
                            .then((res) => {
                                if (res.status === 200 && Array.isArray(res.data?.progress)) {
                                    const map = Object.fromEntries(
                                        res.data.progress.map((p) => [p.itemId, p.status || 'not_started']),
                                    );
                                    setProgressMap(map);
                                }
                            })
                            .catch(() => {});
                    }
                }
            })
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [folderId, currentUser]);

    if (loading) return <div className={styles.loading}>Loading project…</div>;

    if (error) {
        return (
            <div>
                <div className={styles.errorMessage}>{error}</div>
                <ProjectsHubBackLink className={styles.backLink} variant="long" />
            </div>
        );
    }

    if (!doc) {
        return (
            <div>
                <p>Project not found.</p>
                <ProjectsHubBackLink className={styles.backLink} variant="long" />
            </div>
        );
    }

    const gp = doc.girderProjects ?? {};
    const lifecycleStatus = normalizeProjectLifecycleStatus(gp.lifecycleStatus);
    const isDraft = lifecycleStatus === PROJECT_LIFECYCLE.DRAFT;
    const isClosed = lifecycleStatus === PROJECT_LIFECYCLE.CLOSED;
    const isArchived = lifecycleStatus === PROJECT_LIFECYCLE.ARCHIVED;
    const isPublished = lifecycleStatus === PROJECT_LIFECYCLE.PUBLISHED;
    const hasUI = !!gp.annotationUI?.id;
    const hasItems = items.length > 0;
    const isUnconfigured = !hasUI || !hasItems;

    const instructions = gp.annotationUI?.config?.instructions ?? '';
    const participants = gp.participants ?? [];
    const myUserId = currentUser ? String(currentUser._id) : null;
    const myEntry = participants.find((p) => String(p.userId) === myUserId);
    const myRoles = myEntry?.roles ?? [];

    const isAdmin = myRoles.includes('admin') || currentUser?.admin;
    const isAnnotator = myRoles.includes('annotator');
    const isReviewer = myRoles.includes('reviewer');

    const canOpen = isAnnotator && !!firstItemId && isPublished;
    const projectNotReadyForParticipant = !isPublished || !hasUI || !hasItems;

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <ProjectsHubBackLink className={styles.backLink} variant="short" />
                    <h3 className={styles.pageTitle}>{doc.name}</h3>
                    {myRoles.length > 0 && (
                        <div className={styles.myRoles}>
                            {myRoles.map((r) => (
                                <RoleBadge
                                    key={r}
                                    role={r}
                                    variant="long"
                                    className={`${styles.roleBadge} ${styles[`roleBadge_${r}`] || ''}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
                {isAdmin && (
                    <div className={styles.headerActions}>
                        <button
                            type="button"
                            className={styles.buttonSecondary}
                            onClick={() => navigate(`/projects/${folderId}`)}
                        >
                            ✎ Manage project
                        </button>
                    </div>
                )}
            </div>

            {/* Admin: draft or unconfigured empty-state card */}
            {isAdmin && (isDraft || isUnconfigured) && (
                <div className={styles.emptyStateCard}>
                    <h4 className={styles.emptyStateTitle}>
                        {isDraft
                            ? 'This project is in draft mode'
                            : isClosed
                                ? 'This project is closed'
                                : isArchived
                                    ? 'This project is archived'
                                    : 'This project needs setup'}
                    </h4>
                    <ul className={styles.emptyStateList}>
                        {!hasUI && <li>Choose an annotation UI in project settings.</li>}
                        {!hasItems && <li>Add items to the project.</li>}
                        {!instructions && <li>Add instructions for participants (optional).</li>}
                        {isDraft && <li>When ready, publish the project.</li>}
                        {isClosed && <li>Reopen this project to let participants annotate again.</li>}
                        {isArchived && <li>Unarchive/reopen if you want participants to see this project again.</li>}
                    </ul>
                    <div className={styles.emptyStateActions}>
                        <button
                            type="button"
                            className={styles.buttonPrimary}
                            onClick={() => navigate(`/projects/${folderId}`)}
                        >
                            ✎ Manage project
                        </button>
                    </div>
                </div>
            )}

            {/* Instructions */}
            {instructions ? (
                <div className={styles.instructionsCard}>
                    <h4 className={styles.sectionTitle}>Instructions</h4>
                    <p className={styles.instructionsText}>{instructions}</p>
                </div>
            ) : (
                <div className={styles.instructionsCard}>
                    <p className={styles.noInstructions}>
                        {isAdmin
                            ? 'No instructions have been provided for this project.'
                            : 'The project admin has not added instructions yet.'}
                    </p>
                </div>
            )}

            {/* Annotator: one card — annotation task umbrella, Open top-right, then per-item progress */}
            {isAnnotator && (
                <div className={styles.actionCard}>
                    {projectNotReadyForParticipant ? (
                        <>
                            <h4 className={styles.sectionTitle}>Annotation task</h4>
                            <p className={styles.actionDescription}>
                                {isDraft
                                    ? 'This project is being set up. Once published, it will appear with items to annotate.'
                                    : isClosed
                                        ? 'This project is closed. Annotation is currently disabled.'
                                        : isArchived
                                            ? 'This project is archived and not available for annotation.'
                                            : 'This project is not ready for participant annotation yet.'}
                            </p>
                        </>
                    ) : (
                        <>
                            <div className={styles.annotationTaskHeader}>
                                <h4 className={styles.sectionTitle}>Annotation task</h4>
                                <button
                                    type="button"
                                    className={styles.buttonOpen}
                                    onClick={() => navigate(`/projects/${folderId}/view/${firstItemId}`)}
                                    disabled={!canOpen}
                                    title={!firstItemId ? 'No items available yet' : 'Open annotation viewer'}
                                >
                                    Open ▶
                                </button>
                            </div>
                            <p className={styles.actionDescription}>
                                {firstItemId
                                    ? 'Open the annotation viewer to begin or resume your work.'
                                    : 'No items have been added to this project yet.'}
                            </p>
                            {items.length > 0 && (
                                <>
                                    <h5 className={styles.annotationSubheading}>Your progress</h5>
                                    <p className={styles.actionDescription}>
                                        Click an item to open it in the annotation viewer.
                                    </p>
                                    <div className={styles.progressListWrap}>
                                        <ProjectItemListWithProgress
                                            items={items}
                                            progressMap={progressMap}
                                            onSelectItem={(item) => navigate(`/projects/${folderId}/view/${item._id}`)}
                                        />
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Reviewer placeholder */}
            {isReviewer && !isAnnotator && (
                <div className={styles.actionCard}>
                    <h4 className={styles.sectionTitle}>Review task</h4>
                    <p className={styles.actionDescription}>
                        {projectNotReadyForParticipant
                            ? (isDraft
                                ? 'Items will appear here once the project is configured and published.'
                                : isClosed
                                    ? 'This project is closed. Review access is currently disabled.'
                                    : 'This project is archived and hidden from participant workflows.')
                            : 'Review functionality is coming soon.'}
                    </p>
                </div>
            )}

            {/* Admin-only: no other role */}
            {isAdmin && !isAnnotator && !isReviewer && (
                <div className={styles.actionCard}>
                    <p className={styles.actionDescription}>
                        You are a project admin. Use "Manage project" above to configure items and participants.
                    </p>
                </div>
            )}
        </div>
    );
}

export default ProjectHomepage;
